import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchMyTours } from '../../services/tourService';
import { isTourPast, describeBookings } from '../../utils/tourSchedule';
import { TourBuilder } from '../Tours/TourBuilder';
import { ErrorBoundary } from '../common/ErrorBoundary';
import { TourDetailModal } from '../Tours/TourDetailModal';
import { OrganizerPayouts } from '../Profile/OrganizerPayouts';
import { useOrganizerPayouts } from '../../hooks/useOrganizerPayouts';
import './BusinessDashboard.css';

const TABS = [
  { id: 'events',   label: 'Events' },
  { id: 'bookings', label: 'Bookings' },
  { id: 'payouts',  label: 'Payouts' },
  { id: 'settings', label: 'Settings' },
];

const SERVICE_LABELS = { tours: 'Guided Tours', lessons: 'Coaching & Lessons' };

function StatusPill({ tour }) {
  const map = { published: ['Published', '#16a34a'], draft: ['Draft', '#d97706'], pending_payment: ['Pending', '#d97706'], cancelled: ['Cancelled', '#6b7280'] };
  const [label, color] = map[tour.status] ?? [tour.status, '#6b7280'];
  return <span className="bd-pill" style={{ color, background: color + '18' }}>{label}</span>;
}

function EventRow({ tour, onEdit, onView }) {
  return (
    <li className="bd-row">
      {tour.imageUrls?.[0]
        ? <img className="bd-row__thumb" src={tour.imageUrls[0]} alt="" loading="lazy" />
        : <div className="bd-row__thumb bd-row__thumb--ph">{tour.name?.[0]?.toUpperCase()}</div>}
      <div className="bd-row__info">
        <div className="bd-row__top">
          <span className="bd-row__name">{tour.name}</span>
          <span className="bd-row__cat">{tour.category === 'lesson' ? 'Lesson' : 'Tour'}</span>
          <StatusPill tour={tour} />
        </div>
        <span className="bd-row__meta">{describeBookings(tour)}</span>
      </div>
      <div className="bd-row__actions">
        <button className="bd-btn bd-btn--ghost" onClick={() => onView(tour)}>View</button>
        <button className="bd-btn bd-btn--outline" onClick={() => onEdit(tour)}>Edit</button>
      </div>
    </li>
  );
}

function EventsTab({ loading, upcoming, past, onCreate, onEdit, onView }) {
  return (
    <div className="bd-tab">
      <div className="bd-tab__header">
        <h3 className="bd-tab__title">Your events</h3>
        <button className="bd-btn bd-btn--primary" onClick={onCreate}>+ New tour or lesson</button>
      </div>
      {loading && <div className="bd-empty">Loading…</div>}
      {!loading && upcoming.length === 0 && past.length === 0 && (
        <div className="bd-empty">No tours or lessons yet — create your first one above.</div>
      )}
      {upcoming.length > 0 && (
        <>
          <div className="bd-section-title">Upcoming <span className="bd-section-count">{upcoming.length}</span></div>
          <ul className="bd-list">{upcoming.map(t => <EventRow key={t.id} tour={t} onEdit={onEdit} onView={onView} />)}</ul>
        </>
      )}
      {past.length > 0 && (
        <>
          <div className="bd-section-title">Past &amp; cancelled <span className="bd-section-count">{past.length}</span></div>
          <ul className="bd-list">{past.map(t => <EventRow key={t.id} tour={t} onEdit={onEdit} onView={onView} />)}</ul>
        </>
      )}
    </div>
  );
}

function BookingsTab({ loading, tours, onView }) {
  return (
    <div className="bd-tab">
      <div className="bd-tab__header">
        <h3 className="bd-tab__title">Bookings</h3>
      </div>
      <p className="bd-tab__desc">Per-event booking counts for your upcoming tours &amp; lessons. Open an event to see and manage individual attendees.</p>
      {loading && <div className="bd-empty">Loading…</div>}
      {!loading && tours.length === 0 && <div className="bd-empty">No upcoming events with bookings yet.</div>}
      {tours.length > 0 && (
        <ul className="bd-list">
          {tours.map(t => (
            <li className="bd-row" key={t.id}>
              <div className="bd-row__info">
                <div className="bd-row__top">
                  <span className="bd-row__name">{t.name}</span>
                  <span className="bd-row__cat">{t.category === 'lesson' ? 'Lesson' : 'Tour'}</span>
                </div>
                <span className="bd-row__meta">{describeBookings(t)}</span>
              </div>
              <div className="bd-row__actions">
                <button className="bd-btn bd-btn--outline" onClick={() => onView(t)}>View attendees</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SettingsTab({ profile, onOpenProfile }) {
  const services = Array.isArray(profile?.businessServices) ? profile.businessServices : [];
  return (
    <div className="bd-tab">
      <div className="bd-tab__header">
        <h3 className="bd-tab__title">Business settings</h3>
        <button className="bd-btn bd-btn--outline" onClick={onOpenProfile}>Edit in Profile</button>
      </div>
      <dl className="bd-summary">
        <dt>Business name</dt><dd>{profile?.businessName || '—'}</dd>
        <dt>Contact email</dt><dd>{profile?.businessEmail || '—'}</dd>
        <dt>Contact phone</dt><dd>{profile?.businessPhone || '—'}</dd>
        <dt>Website</dt><dd>{profile?.businessWebsite || '—'}</dd>
        <dt>Services</dt><dd>{services.length ? services.map(s => SERVICE_LABELS[s] || s).join(', ') : '—'}</dd>
        <dt>ABN</dt><dd>{profile?.businessABN || '—'}</dd>
        <dt>Insurance on file</dt><dd>{profile?.businessInsuranceFileName || '—'}</dd>
      </dl>
    </div>
  );
}

// Full-viewport surface for verified businesses to manage their events,
// bookings, and payouts — the Dashboard "expands to full screen" surface
// requested alongside the tour/lesson creation gate.
export function BusinessDashboard({ onClose, onOpenProfile }) {
  const { user, profile } = useAuth();
  const [tab, setTab] = useState('events');
  const [tours, setTours] = useState(null);
  const [editTour, setEditTour] = useState(null);
  const [showTourBuilder, setShowTourBuilder] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);

  const { connectStatus, connectLoading, connectError, handleConnectStripe } = useOrganizerPayouts(user);

  function refreshTours() {
    if (!user) return;
    fetchMyTours(user.uid).then(setTours).catch(() => setTours([]));
  }
  useEffect(refreshTours, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Defense-in-depth: the "Dashboard" menu item is already gated in
  // AuthBadge — this just covers stale state (e.g. a status change mid-session).
  if (profile?.businessVerifiedStatus !== 'verified') {
    return createPortal(
      <div className="bd-overlay">
        <div className="bd-guard">
          <p>Only verified businesses can access the Dashboard.</p>
          <button className="bd-btn bd-btn--outline" onClick={onClose}>Close</button>
        </div>
      </div>,
      document.body
    );
  }

  const loading = tours === null;
  const upcoming = (tours || []).filter(t => !isTourPast(t) && t.status !== 'cancelled');
  const past = (tours || []).filter(t => isTourPast(t) || t.status === 'cancelled');

  return createPortal(
    <div className="bd-overlay">
      <div className="bd-shell">
        <div className="bd-topbar">
          <div className="bd-topbar__title">
            <span className="bd-topbar__name">{profile.businessName || 'Business Dashboard'}</span>
          </div>
          <button className="bd-topbar__close" onClick={onClose}>← Back to map</button>
        </div>

        <div className="bd-body">
          <nav className="bd-nav">
            {TABS.map(t => (
              <button
                key={t.id}
                className={`bd-nav__item${tab === t.id ? ' bd-nav__item--active' : ''}`}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </nav>

          <div className="bd-content">
            {tab === 'events' && (
              <EventsTab
                loading={loading}
                upcoming={upcoming}
                past={past}
                onCreate={() => { setEditTour(null); setShowTourBuilder(true); }}
                onEdit={t => { setEditTour(t); setShowTourBuilder(true); }}
                onView={t => setSelectedTour(t)}
              />
            )}
            {tab === 'bookings' && (
              <BookingsTab loading={loading} tours={upcoming} onView={t => setSelectedTour(t)} />
            )}
            {tab === 'payouts' && (
              <div className="bd-tab">
                <OrganizerPayouts
                  connectStatus={connectStatus}
                  connectLoading={connectLoading}
                  connectError={connectError}
                  onConnectStripe={handleConnectStripe}
                />
              </div>
            )}
            {tab === 'settings' && (
              <SettingsTab profile={profile} onOpenProfile={onOpenProfile} />
            )}
          </div>
        </div>
      </div>

      {showTourBuilder && (
        <ErrorBoundary onClose={() => { setShowTourBuilder(false); setEditTour(null); }}>
          <TourBuilder
            onClose={() => { setShowTourBuilder(false); setEditTour(null); }}
            onOpenProfile={onOpenProfile}
            editTour={editTour}
            onSaved={refreshTours}
          />
        </ErrorBoundary>
      )}
      {selectedTour && (
        <TourDetailModal
          tour={selectedTour}
          onClose={() => setSelectedTour(null)}
          onCancelled={() => { setSelectedTour(null); refreshTours(); }}
          onEdit={t => { setSelectedTour(null); setEditTour(t); setShowTourBuilder(true); }}
        />
      )}
    </div>,
    document.body
  );
}
